import { AssetGroup, PartialRecord } from '@play-co/timestep-core';
import {
  ImageViewOpts,
  View,
  ImageCoverView,
  ImageScaleView,
  ImageScaleViewOpts,
} from '@play-co/timestep-core/ui';
import { useCache } from 'src/lib/utils';
import { getMatch2AssetPath } from './game/util/assets';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getMatch2RewardAsset } from './rewards/match2-rewards.utils';
import { getMatch2ContinueConfig } from 'src/replicant/match2/match2.getters';
import { user } from 'src/StateObserver';
import { PowerBlockType } from './game/defs/block';
import { getPowerBlockMap } from './game/defs/powerBlocks';
import { Match2BuruStreakRewardSet } from 'src/replicant/match2/match2.ruleset';
import { DaysOfTheWeek } from 'src/replicant/utils/time';
import { Match2FrenzyTheme } from 'src/replicant/match2/frenzy/match2-frenzy.ruleset';

/**
 * Generates a frenzy objective icon for the given theme.
 * @param theme
 * @returns
 */
export function getFrenzyObjectiveIcon(theme: Match2FrenzyTheme) {
  const cache = useCache(
    async () => {
      const { rocket } = getPowerBlockMap();
      await new AssetGroup([
        getMatch2AssetPath(rocket),
        getMatch2AssetPath('basic/block.basic.green.png'),
        getMatch2AssetPath('symbol.club.png'),
      ]).load();

      const themeImageMap: Record<
        Match2FrenzyTheme,
        { bg: ImageViewOpts; icon: ImageScaleViewOpts }
      > = {
        firecracker: {
          bg: {
            image: getMatch2AssetPath(rocket),
            r: -Math.PI / 4,
          },
          icon: { visible: false },
        },
        leaf: {
          bg: {
            image: getMatch2AssetPath('basic/block.basic.green.png'),
            r: -Math.PI / 15,
            scale: 0.9,
          },

          icon: {
            width: 86 * 0.6,
            height: 80 * 0.6,
            x: 36,
            y: 36,
            centerOnOrigin: true,
            scaleMethod: 'contain',
            visible: true,
            image: getMatch2AssetPath('symbol.club.png'),
          },
        },
      };

      const view = new View({
        width: 90,
        height: 90,
      });

      const bg = new ImageCoverView({
        superview: view,
        width: 86 * 0.9,
        height: 80 * 0.9,
        mode: 'contain',
        centerOnOrigin: true,
        centerAnchor: true,
        x: view.style.width * 0.5,
        y: view.style.height * 0.5,
        ...themeImageMap[theme].bg,
      });

      const icon = new ImageScaleView({
        superview: bg,
        ...themeImageMap[theme].icon,
      });

      bg.updateOpts(themeImageMap[theme].bg);
      icon.updateOpts(themeImageMap[theme].icon);

      return view.style.renderToImage();
    },
    `frenzyObjectiveIcon-${theme}`,
    Infinity,
  );

  // Preload the image if it's not already cached.
  if (!cache.getCached()) {
    void cache.get();
  }

  return cache;
}

/**
 * Generates a reward icon with given rewards.
 * @param rewards
 * @returns
 */
export function getRewardIcon(rewards: Match2Reward[]) {
  const cache = useCache(
    async () => {
      const icons = (
        await Promise.all(rewards.map((reward) => getMatch2RewardAsset(reward)))
      ).filter((x) => typeof x === 'string') as string[];

      await new AssetGroup(icons).load();
      const iconSize = 60;
      const view = new View({
        width: iconSize * 2,
        height: iconSize * 2,
      });

      // Start from the center of the view.
      const originX = view.style.width * 0.5;
      const originY = view.style.height * 0.5;

      // If it's a single reward, just show the icon.
      if (rewards.length === 1) {
        new ImageScaleView({
          superview: view,
          x: originX,
          y: originY,
          width: iconSize * 1.3,
          height: iconSize * 1.3,
          image: icons[0],
          centerOnOrigin: true,
          scaleMethod: 'contain',
        });
        return view.style.renderToImage();
      }

      // Place the icons in a circle.
      const angle = (2 * Math.PI) / icons.length;
      const radius = iconSize * 0.4;

      for (let i = 0; i < icons.length; i++) {
        const iconAngle = angle * i - (1.2 * Math.PI) / 2;
        new ImageScaleView({
          superview: view,
          width: iconSize * 1.5,
          height: iconSize * 1.2,
          image: icons[i],
          x: originX + radius * Math.cos(iconAngle),
          y: originY + radius * Math.sin(iconAngle),
          centerOnOrigin: true,
          scaleMethod: 'contain',
        });
      }

      return view.style.renderToImage();
    },
    `rewardIcon-${rewards.map((reward) => reward.type).join('-')}`,
    Infinity,
  );

  // Preload the image if it's not already cached.
  if (!cache.getCached()) {
    void cache.get();
  }

  return cache;
}

/**
 * Generate continue reward icon.
 */
export function getContinueRewardIcon() {
  const { powerups } = getMatch2ContinueConfig(user());
  if (!powerups) return undefined;

  const rewards: Match2Reward[] = powerups.map((powerup) => ({
    type: 'powerups',
    subType: powerup.type as PowerBlockType,
    amount: powerup.amount,
  }));

  return getRewardIcon(rewards);
}

/**
 * Generate reward icon for streak rewards.
 * @param reward
 */
export function getStreakRewardIcon(reward: Match2BuruStreakRewardSet) {
  const rewards: Match2Reward[] = Object.keys(reward.rewards).map((key) => ({
    type: 'powerups',
    subType: key as PowerBlockType,
    amount:
      reward.rewards[key as keyof Match2BuruStreakRewardSet['rewards']] ?? 1,
  }));

  return getRewardIcon(rewards);
}

/**
 * Generates daily challenge objective icon for the given day.
 * @param theme
 * @returns
 */
export function getDailyChallengeObjectiveIcon(day: DaysOfTheWeek) {
  const cache = useCache(
    async () => {
      const { bomb, rocket, cube } = getPowerBlockMap();
      await new AssetGroup([
        getMatch2AssetPath(rocket),
        getMatch2AssetPath(bomb),
        getMatch2AssetPath(cube),
        getMatch2AssetPath('basic/block.basic.yellow.png'),
        getMatch2AssetPath('symbol.diamond.png'),
      ]).load();

      const map: PartialRecord<
        DaysOfTheWeek,
        { bg: ImageViewOpts; icon: ImageScaleViewOpts }
      > = {
        monday: {
          bg: {
            image: getMatch2AssetPath(bomb),
          },
          icon: { visible: false },
        },
        saturday: {
          bg: {
            image: getMatch2AssetPath(rocket),
            r: -Math.PI / 4,
          },
          icon: { visible: false },
        },
        wednesday: {
          bg: {
            image: getMatch2AssetPath(cube),
          },
          icon: { visible: false },
        },
        friday: {
          bg: {
            image: getMatch2AssetPath('basic/block.basic.yellow.png'),
          },
          icon: {
            width: 86 * 0.6,
            height: 80 * 0.6,
            x: 36,
            y: 36,
            centerOnOrigin: true,
            scaleMethod: 'contain',
            visible: true,
            image: getMatch2AssetPath('symbol.diamond.png'),
          },
        },
      };

      const dayConfig = map[day];
      if (!dayConfig) {
        return `assets/ui/match2/daily_challenges/${day}/icon_${day}.png`;
      }

      const view = new View({
        width: 90,
        height: 90,
      });

      const bg = new ImageCoverView({
        superview: view,
        width: 86 * 0.9,
        height: 80 * 0.9,
        mode: 'contain',
        centerOnOrigin: true,
        centerAnchor: true,
        x: view.style.width * 0.5,
        y: view.style.height * 0.5,
        ...dayConfig.bg,
      });

      const icon = new ImageScaleView({
        superview: bg,
        ...dayConfig.icon,
      });

      bg.updateOpts(dayConfig.bg);
      icon.updateOpts(dayConfig.icon);

      return view.style.renderToImage();
    },
    `dailyCahllengeObjective-${day}`,
    Infinity,
  );

  // Preload the image if it's not already cached.
  if (!cache.getCached()) {
    void cache.get();
  }

  return cache;
}
